import { ExternalLinkIcon } from "@heroicons/react/outline";

import { LabStationList } from "custom-models/frontLoader";

import { useAppSelector } from "app/hooks";

import "gates/StationSelectionGate.css";

export const StationSelection = ({
  labId,
  setLabId,
  stationId,
  setStationId,
  hideLabSelection,
  defaultStationId,
}: {
  labId?: number;
  setLabId: (id: number) => void;
  stationId?: number;
  setStationId: (id: number) => void;
  hideLabSelection?: boolean;
  defaultStationId?: number;
}) => {
  const labStations = useAppSelector((state) => state.labStations);
  const labs = useAppSelector((state) => state.labs);

  const labCount = Object.values(labs).length;

  const labStationTypes = [
    "accessioning",
    "grossing",
    "embedding",
    "cutting",
    "scanning",
  ];

  const filteredLabStations = Object.values(labStations).filter(
    (labStation) =>
      labStation.lab_id === labId &&
      labStation.type &&
      labStationTypes.includes(labStation.type)
  );
  const groupedLabStations = filteredLabStations.reduce(
    (groups, labStation) => {
      const stationType = labStation.type || "other";
      if (!groups[stationType]) groups[stationType] = [];
      groups[stationType].push(labStation);
      return groups;
    },
    {} as { [key: string]: LabStationList[] }
  );

  if (labCount < 1)
    return (
      <div>
        You do not have any Lab Memberships. Contact an administrator for
        assistance.
      </div>
    );

  return (
    <div className="StationSelectionGate">
      {!hideLabSelection && (labCount > 1 || !labId) ? (
        <div className="StationSelectionGate--LabSelection">
          <label htmlFor="lab-selection">Lab: </label>
          <select
            id="lab-selection"
            value={labId || ""}
            onChange={(ev) => setLabId(parseInt(ev.currentTarget.value))}
          >
            <option value="">-- Select a Lab --</option>
            {Object.values(labs)
              .sort((labA, labB) =>
                (labA.name ?? "").localeCompare(labB.name ?? "")
              )
              .map((lab) => (
                <option key={lab.id} value={lab.id}>
                  {lab.name || "UNKNOWN"}
                </option>
              ))}
          </select>
        </div>
      ) : (
        <div>Lab: {labs[labId || 0]?.name}</div>
      )}

      {!!labId && filteredLabStations.length < 1 && (
        <div>
          There are no Stations available for this Lab.{" "}
          {labCount > 1 ? "Select a different Lab or c" : "C"}ontact an
          administrator for assistance.
        </div>
      )}

      {!!labId && filteredLabStations.length > 0 && (
        <div className="StationSelectionGate--StationSelection">
          <div className="StationSelectionGate--Header">Select a Station:</div>
          {!stationId && (
            <div className="StationSelectionGate--SubHeader">
              This selection only needs to be made once on this device
            </div>
          )}
          <div className="StationSelectionGate--StationSelection--Groups">
            {Object.keys(groupedLabStations).map((stationType) => (
              <div
                key={stationType}
                className="StationSelectionGate--StationSelection--Group"
              >
                <div>
                  <div className="StationSelectionGate--StationSelection--Group--Header">
                    {stationType} Stations
                  </div>

                  {groupedLabStations[stationType].map((labStation) => (
                    <div key={labStation.id}>
                      <button
                        className={`StationSelectionGate--StationSelection--Group--Button ${
                          stationId === labStation.id ? "Current" : ""
                        }`}
                        type="button"
                        key={labStation.id}
                        onClick={() => setStationId(labStation.id)}
                        disabled={stationId === labStation.id}
                      >
                        {labStation.name || "UNKNOWN"}
                        {labStation.description &&
                          labStation.name !== labStation.description && (
                            <> ({labStation.description})</>
                          )}
                        {defaultStationId === labStation.id && <sup>*</sup>}
                      </button>

                      {!!stationId && (
                        <a
                          className="StationSelectionGate--StationSelection--Group--Button--right"
                          target="_blank"
                          rel="noreferrer"
                          href={
                            (labStation.type === "accessioning"
                              ? "/cases/"
                              : `/station/${labStation.type}/`) +
                            `?station=${labStation.id}`
                          }
                          onClick={(ev) => ev.stopPropagation()}
                        >
                          <ExternalLinkIcon height={24} />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {!!defaultStationId && (
            <small>
              <em>
                <sup>*</sup>
                {labStations[defaultStationId]?.name} is the default station for
                this computer
              </em>
            </small>
          )}
        </div>
      )}
    </div>
  );
};

export default StationSelection;
